import { MutableRefObject, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  type Edge,
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { Box, Text, useClassNames } from '@factorialco/deprecated-design-system'
import { assignInlineVars } from '@vanilla-extract/dynamic'

import { useEffect } from 'modules/core/hooks/useEffect'
import theme from 'modules/core/lib/theme'

import { cardBorderColor } from 'modules/project_management/components/Kanban/index.vanilla.css'
import * as stylesRefs from 'modules/project_management/components/Kanban/index.vanilla.css'

import { TCard, getCardData, getCardDropTargetData, isCardData, isShallowEqual } from '../utils'

const idleState: TCardState = { type: 'idle' }
type TCardState =
  | {
      type: 'idle'
    }
  | {
      type: 'is-dragging'
    }
  | {
      type: 'is-dragging-and-left-self'
    }
  | {
      type: 'is-over'
      dragging: DOMRect
      closestEdge: Edge
    }
  | {
      type: 'preview'
      container: HTMLElement
      dragging: DOMRect
    }

export const CardShadow = ({ dragging }: { dragging: DOMRect }) => {
  return (
    <Box
      borderRadius={{ all: 'abs012' }}
      background="grey300"
      style={{ height: `${dragging.height}px` }}
    />
  )
}

const CardDisplay = <TItem = unknown,>({
  card,
  cardState,
  outerRef,
  innerRef,
  onClickCard,
}: {
  card: TCard<TItem>
  cardState: TCardState
  outerRef?: MutableRefObject<HTMLDivElement | null>
  innerRef?: MutableRefObject<HTMLDivElement | null>
  onClickCard?: () => void
}) => {
  const [hover, setHover] = useState(false)
  const { title, projections } = card
  const cx = useClassNames(stylesRefs)

  return (
    <Box ref={outerRef} paddingBottom="s4">
      {cardState.type === 'is-over' && cardState.closestEdge === 'top' ? (
        <Box paddingBottom="s4">
          <CardShadow dragging={cardState.dragging} />
        </Box>
      ) : null}
      <Box
        ref={innerRef}
        style={{
          opacity: cardState.type === 'is-dragging' ? '0' : '1',
          cursor: 'grab',
          display: cardState.type === 'is-dragging-and-left-self' ? 'none' : 'inline-block',
          ...assignInlineVars({
            [cardBorderColor]: hover ? theme.colors.grey600 : theme.colors.grey300,
          }),
        }}
        background="white"
        onClick={onClickCard}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={cx('kanbanCardStyle')}
      >
        <Box paddingBottom="s16">
          <Text size="200" weight="medium" color="grey1000" overflowWrap>
            {title}
          </Text>
        </Box>
        <Box gap="s12">{projections.map((projection) => projection)}</Box>
      </Box>
      {cardState.type === 'is-over' && cardState.closestEdge === 'bottom' ? (
        <Box paddingTop="s4">
          <CardShadow dragging={cardState.dragging} />
        </Box>
      ) : null}
    </Box>
  )
}

export const KanbanCard = <TItem = unknown,>({
  card,
  columnId,
  onClickCard,
}: {
  card: TCard<TItem>
  columnId: string
  onClickCard?: () => void
}) => {
  const outerRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const [cardState, setCardState] = useState<TCardState>(idleState)

  useEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer || !inner) return

    return combine(
      draggable({
        element: inner,
        getInitialData: ({ element }) =>
          getCardData<TItem>({ card, columnId, rect: element.getBoundingClientRect() }),
        onGenerateDragPreview({ nativeSetDragImage, location, source }) {
          const data = source.data
          if (!isCardData<TItem>(data)) return

          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element: inner, input: location.current.input }),
            render({ container }) {
              setCardState({
                type: 'preview',
                container,
                dragging: inner.getBoundingClientRect(),
              })
            },
          })
        },
        onDragStart() {
          setCardState({ type: 'is-dragging' })
        },
        onDrop() {
          setCardState(idleState)
        },
      }),
      dropTargetForElements({
        element: outer,
        getIsSticky: () => true,
        getData: ({ element, input }) => {
          const data = getCardDropTargetData<TItem>({ card, columnId })

          return attachClosestEdge(data, { element, input, allowedEdges: ['top', 'bottom'] })
        },
        onDragEnter({ source, self }) {
          if (!isCardData<TItem>(source.data)) return
          if (source.data.card.id === card.id) return
          const closestEdge = extractClosestEdge(self.data)
          if (!closestEdge) return

          setCardState({ type: 'is-over', dragging: source.data.rect, closestEdge })
        },
        onDrag({ source, self }) {
          if (!isCardData<TItem>(source.data)) return
          if (source.data.card.id === card.id) return
          const closestEdge = extractClosestEdge(self.data)
          if (!closestEdge) return

          const proposed: TCardState = { type: 'is-over', dragging: source.data.rect, closestEdge }
          setCardState((current) => {
            if (isShallowEqual(proposed, current)) {
              return current
            }
            return proposed
          })
        },
        onDragLeave({ source }) {
          if (!isCardData<TItem>(source.data)) return
          if (source.data.card.id === card.id) {
            setCardState({ type: 'is-dragging-and-left-self' })
            return
          }
          setCardState(idleState)
        },
        onDrop() {
          setCardState(idleState)
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, columnId])

  return (
    <>
      <CardDisplay<TItem>
        outerRef={outerRef}
        innerRef={innerRef}
        cardState={cardState}
        card={card}
        onClickCard={onClickCard}
      />
      {cardState.type === 'preview'
        ? createPortal(
            <CardDisplay<TItem> cardState={cardState} card={card} onClickCard={onClickCard} />,
            cardState.container
          )
        : null}
    </>
  )
}
